import React from 'react';
import { styled } from 'linaria/react';
import { theme } from '../../../Theme';
import SectionWrapper from '../../../Layout/SectionWrapper';
import Image from '../components/Image';

const ImageLayoutWrapper = styled(SectionWrapper)`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
`;

const ContentWrapper = styled('div')`
  display: flex;
  padding-top: 80px;
  padding-bottom: 80px;
  justify-content: ${({ layout }) =>
    layout === 'loose' ? 'space-between' : 'center'};
  align-items: flex-start;
  padding-left: 32px;
  padding-right: 32px;
  gap: ${({ layout }) => (layout === 'tight' ? '8px' : '0px')};
  flex-wrap: wrap;

  ${theme.below.lg} {
    gap: ${({ layout }) => (layout === 'loose' ? '32px' : '8px')};
    justify-content: center;
  }

  ${theme.below.md} {
    justify-content: ${({ layout, first_image_size, second_image_size }) => {
      if (first_image_size === 'small' && layout !== 'center') {
        return 'flex-start';
      } else if (second_image_size === 'small') {
        return 'flex-end';
      } else {
        return 'center';
      }
    }};
  }
`;

const ImageWrapper = styled('div')`
  position: relative;
  max-width: ${({ size, layout }) => {
    if (size === 'fullwidth') {
      return '100%';
    } else if (size === 'small') {
      return '424px';
    } else if (size === 'large' && layout === 'tight') {
      return '598px';
    } else if (size === 'large' && layout === 'loose') {
      return '770px';
    }
  }};
  width: 100%;
  height: auto;
  aspect-ratio: ${({ size, ratio }) => {
    if (size === 'fullwidth') {
      return '16 / 9';
    } else if (ratio === '4:5') {
      return '4 / 5';
    } else if (ratio === '5:4') {
      return '5 / 4';
    }
    return '1 / 1';
  }};
  margin: ${({ size }) => (size === 'fullwidth' ? '32px 0' : '0')};

  ${theme.below.md} {
    max-width: ${({ size }) => (size === 'small' ? '60%' : 'auto')};
  }
`;

const TextWrapper = styled('div')`
  margin-top: 0.5rem;
  color: ${theme.colors.black};
  text-align: left;
  font-size: 14px;
  max-width: 342px;
`;

const TextLink = styled('a')`
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    color: ${theme.colors.black};
  }
`;

const ImageLayout = ({
  layout,
  first_image,
  first_image_link,
  first_image_size,
  first_image_ratio,
  second_image,
  second_image_link,
  second_image_size,
  second_image_ratio,
  text,
  text_link
}) => {
  const firstLink = first_image_link?.cached_url || '#';
  const secondLink = second_image_link?.cached_url || '#';
  const textLink = text_link?.cached_url || '#';

  return (
    <ImageLayoutWrapper>
      <ContentWrapper
        layout={layout}
        first_image_size={first_image_size}
        second_image_size={second_image_size}
        first_image={first_image}
        second_image={second_image}
      >
        <ImageWrapper
          ratio={first_image_ratio}
          size={first_image_size}
          layout={layout}
        >
          {firstLink ? (
            <a href={firstLink} rel="noopener noreferrer">
              <Image
                fillAvailableSpace
                src={first_image?.filename || 'fallback-image.jpg'}
                alt={first_image?.alt || 'No image available'}
              />
            </a>
          ) : (
            <Image
              fillAvailableSpace
              src={first_image?.filename || 'fallback-image.jpg'}
              alt={first_image?.alt || 'No image available'}
            />
          )}
          {textLink ? (
            <TextLink href={textLink} rel="noopener noreferrer">
              <TextWrapper>{text}</TextWrapper>
            </TextLink>
          ) : (
            <TextWrapper>{text}</TextWrapper>
          )}
        </ImageWrapper>
        {layout !== 'center' && second_image && (
          <ImageWrapper
            ratio={second_image_ratio}
            size={second_image_size}
            layout={layout}
          >
            {secondLink ? (
              <a href={secondLink} rel="noopener noreferrer">
                <Image
                  fillAvailableSpace
                  src={second_image?.filename || 'fallback-image.jpg'}
                  alt={second_image?.alt || 'No image available'}
                />
              </a>
            ) : (
              <Image
                fillAvailableSpace
                src={second_image?.filename || 'fallback-image.jpg'}
                alt={second_image?.alt || 'No image available'}
              />
            )}
          </ImageWrapper>
        )}
      </ContentWrapper>
    </ImageLayoutWrapper>
  );
};

export default ImageLayout;
